<script setup lang="ts">
import { watch } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import { LiabilityType } from '@/helpers/const';
import type { ApplicantLiability, OtherLiability } from '@/types';

interface SimpleLiabilityFormValues {
  monthlyRepaymentAmount: number;
}

const props = defineProps<{
  applicantId: string;
  liabilities: ApplicantLiability[];
}>();

const emit = defineEmits<{
  (e: 'update:liabilities', v: ApplicantLiability[]): void;
}>();

const simpleLiabilitySchema = yup.object({
  monthlyRepaymentAmount: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required('Monthly repayment amount is required')
    .min(0, 'Amount cannot be negative'),
});

const { meta, submitCount, handleSubmit, resetForm } = useForm<SimpleLiabilityFormValues>({
  validationSchema: simpleLiabilitySchema,
  validateOnMount: false,
});

watch(
  () => props.liabilities,
  (newLiabilities) => {
    const firstLiability = newLiabilities[0];
    let amount = 0;

    if (firstLiability && firstLiability.type === LiabilityType.OTHER) {
      amount = (firstLiability as OtherLiability).periodicRepayment?.amount ?? 0;
    }

    resetForm({
      values: {
        monthlyRepaymentAmount: amount,
      },
    });
  },
  { immediate: true, deep: true },
);

const update = handleSubmit(
  (formValues) => {
    const repaymentValue = formValues.monthlyRepaymentAmount;

    const newSimpleLiability: OtherLiability = {
      type: LiabilityType.OTHER,
      details: '',
      liabilityLimit: 0,
      amountOwing: 0,
      periodicRepayment: {
        amount: repaymentValue,
        frequency: 'month',
      },
      liabilityNumber: 0,
    };

    emit('update:liabilities', [newSimpleLiability]);
    return true;
  },
  () => {
    return false;
  },
);

defineExpose({ update });
</script>

<template>
  <BxField v-slot="{ field }" name="monthlyRepaymentAmount">
    <BxInput
      :id="`${applicantId}-monthly-repayment`"
      v-bind="field"
      class="form-control"
      type="number"
      label="Monthly repayments amount"
      label-type="floating"
      leading-symbol="$"
    />
  </BxField>
  <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
    Please review the error messages above.
  </BxErrorPanel>
</template>
